import { type UseModalState, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { type TierType } from "@src/appV2/redesign/Tiers/api/schema";
import { useGetTiers } from "@src/appV2/redesign/Tiers/api/useGetTiers";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

interface UseShowImprovedTierBottomSheetResponse {
  workerTier: TierType | undefined;
  workerId: string;
  improvedTierModalState: UseModalState;
}

function tierImproved(
  lastKnownTier: string | undefined,
  newTier: TierType
): newTier is "GOLD" | "SILVER" {
  const improvedToGold =
    ["SILVER", "LIMITED", undefined].includes(lastKnownTier) && newTier === "GOLD";
  const improvedToSilver = ["LIMITED", undefined].includes(lastKnownTier) && newTier === "SILVER";
  return improvedToGold || improvedToSilver;
}

export function useShowImprovedTierBottomSheet(): UseShowImprovedTierBottomSheetResponse {
  const worker = useDefinedWorker();
  const tierConfiguration = useCbhFlag(CbhFeatureFlag.CONFIG_TIERS, {
    defaultValue: { enabled: false },
  });
  const { enabled } = tierConfiguration;
  const { data: tiers, isSuccess } = useGetTiers(
    { workerIds: [worker?.userId] },
    { enabled: isDefined(worker?.userId) && enabled }
  );
  const lastKnownTier = localStorage.getItem("lastKnownTier") ?? undefined;
  const improvedTierModalState = useModalState("closed");
  const successfullyLoadedTier = isSuccess && tiers.data.length > 0;
  if (successfullyLoadedTier && enabled) {
    const newTier = tiers.data[0].attributes.type;
    localStorage.setItem("lastKnownTier", newTier);
    if (tierImproved(lastKnownTier, newTier)) {
      improvedTierModalState.openModal();
      return {
        workerTier: newTier,
        workerId: worker.userId,
        improvedTierModalState,
      };
    }

    return {
      workerTier: newTier,
      workerId: worker.userId,
      improvedTierModalState,
    };
  }

  return {
    workerTier: undefined,
    workerId: worker.userId,
    improvedTierModalState,
  };
}
